import { Controller } from "@hotwired/stimulus"

// Searchable select (Select2-style) used by filters and form fields.
// Options are client-side filtered; no remote search dependency.
export default class extends Controller {
  static targets = ["button", "menu", "input", "label", "option", "check", "empty", "search", "list", "suffix"]
  static values = {
    placeholder: String,
    suffixFallback: { type: String, default: "" }
  }

  connect() {
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("click", this.handleDocumentClick)
    this.highlightedIndex = -1
    this.sync()
  }

  disconnect() {
    document.removeEventListener("click", this.handleDocumentClick)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.menuTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    // Lift above sibling rows so the menu isn't clipped/covered in stacked forms.
    this.element.classList.add("z-30")
    this.menuTarget.classList.remove("hidden")
    this.searchTarget.value = ""
    this.filter()
    this.highlightedIndex = -1
    this.clearHighlight()
    // Focus after paint so mobile keyboards open reliably.
    requestAnimationFrame(() => this.searchTarget.focus())
  }

  close() {
    this.element.classList.remove("z-30")
    this.menuTarget.classList.add("hidden")
    this.searchTarget.value = ""
    this.highlightedIndex = -1
    this.clearHighlight()
  }

  select(event) {
    event.preventDefault()
    event.stopPropagation()
    this.applySelection(event.currentTarget)
  }

  applySelection(option) {
    if (!option) return

    const value = option.dataset.comboboxOptionValue || ""
    const label = option.dataset.comboboxOptionLabel || this.placeholderValue
    const unit = option.dataset.comboboxOptionUnit || ""

    this.inputTarget.value = value
    this.labelTarget.textContent = value === "" ? this.placeholderValue : label
    this.updateSuffix(unit)
    this.close()
    this.filter()
    this.sync()

    // Native-like change event so other controllers/forms can react.
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.inputTarget.dispatchEvent(new CustomEvent("combobox:change", {
      bubbles: true,
      detail: { value, label, unit }
    }))
  }

  filter() {
    const query = this.searchTarget.value.trim().toLowerCase()
    let visibleCount = 0

    this.optionTargets.forEach((option) => {
      const label = (option.dataset.comboboxOptionLabel || "").toLowerCase()
      const visible = query === "" || label.includes(query)
      option.classList.toggle("hidden", !visible)
      if (visible) visibleCount += 1
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visibleCount > 0)
    }

    this.highlightedIndex = -1
    this.clearHighlight()
  }

  keydown(event) {
    if (this.menuTarget.classList.contains("hidden")) return

    const visible = this.visibleOptions()

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, visible.length - 1)
        this.applyHighlight(visible)
        break
      case "ArrowUp":
        event.preventDefault()
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
        this.applyHighlight(visible)
        break
      case "Enter":
        event.preventDefault()
        if (this.highlightedIndex >= 0 && visible[this.highlightedIndex]) {
          this.applySelection(visible[this.highlightedIndex])
        }
        break
      case "Escape":
        event.preventDefault()
        this.close()
        this.buttonTarget.focus()
        break
    }
  }

  sync() {
    const selectedValue = this.inputTarget.value
    const selectedOption = this.optionTargets.find(
      (option) => option.dataset.comboboxOptionValue === selectedValue
    )
    const label = selectedOption?.dataset.comboboxOptionLabel || this.placeholderValue

    this.labelTarget.textContent = selectedValue === "" ? this.placeholderValue : label
    this.buttonTarget.classList.toggle("text-slate-400", selectedValue === "")
    this.buttonTarget.classList.toggle("text-slate-700", selectedValue !== "")
    this.buttonTarget.classList.toggle("text-slate-900", selectedValue !== "")

    this.checkTargets.forEach((check) => {
      const selected = check.dataset.comboboxCheckValue === selectedValue
      check.classList.toggle("opacity-100", selected)
      check.classList.toggle("opacity-0", !selected)
    })

    if (selectedOption) {
      this.updateSuffix(selectedOption.dataset.comboboxOptionUnit || "")
    } else {
      this.updateSuffix("")
    }
  }

  updateSuffix(unit) {
    if (!this.hasSuffixTarget) return

    const text = (unit || this.suffixFallbackValue || "").toString().toLowerCase()
    this.suffixTarget.textContent = text
    // Suffix target is optional chrome; keep it empty when unused.
    this.suffixTarget.classList.toggle("hidden", text === "")
  }

  visibleOptions() {
    return this.optionTargets.filter((option) => !option.classList.contains("hidden"))
  }

  applyHighlight(visible) {
    this.clearHighlight()
    const option = visible[this.highlightedIndex]
    if (!option) return

    option.classList.add("bg-slate-100")
    option.scrollIntoView({ block: "nearest" })
  }

  clearHighlight() {
    this.optionTargets.forEach((option) => option.classList.remove("bg-slate-100"))
  }

  handleDocumentClick(event) {
    if (this.element.contains(event.target)) return
    this.close()
  }
}
